import { apiClient } from './apiClient';

const FLASHCARD_DECKS_API = '/api/app/flashcard-decks-extn';

export interface FlashcardDeckDto {
  id: string;
  bookId: string;
  userId: string;
  name: string;
  description?: string;
  cardCount: number;
  creationTime: string;
  concurrencyStamp?: string;
}

export interface FlashcardDeckCreateDto {
  bookId: string;
  name: string;
  description?: string;
}

export interface FlashcardDeckUpdateDto {
  bookId: string;
  userId: string;
  name: string;
  description?: string;
  concurrencyStamp: string;
}

export const flashcardDecksService = {
  /**
   * Get all flashcard decks for current user and specific book
   */
  async getMyDecks(bookId: string): Promise<FlashcardDeckDto[]> {
    const response = await apiClient.get<FlashcardDeckDto[]>(
      `${FLASHCARD_DECKS_API}/my-decks`,
      { params: { bookId } }
    );
    return response.data;
  },

  /**
   * Create a flashcard deck for current user
   */
  async createMyDeck(input: FlashcardDeckCreateDto): Promise<FlashcardDeckDto> {
    const response = await apiClient.post<FlashcardDeckDto>(
      `${FLASHCARD_DECKS_API}/my-deck`,
      input
    );
    return response.data;
  },

  /**
   * Update a flashcard deck (current user only)
   * Fetches current deck first to get concurrencyStamp
   */
  async updateMyDeck(
    id: string,
    bookId: string,
    input: Partial<Omit<FlashcardDeckUpdateDto, 'bookId' | 'userId' | 'concurrencyStamp'>>
  ): Promise<FlashcardDeckDto> {
    // Fetch all decks to find the one we're updating
    const decks = await this.getMyDecks(bookId);
    const currentDeck = decks.find((d) => d.id === id);

    if (!currentDeck) {
      throw new Error(`Flashcard deck with id ${id} not found`);
    }

    // Merge with update data
    const updateDto: FlashcardDeckUpdateDto = {
      name: input.name || currentDeck.name,
      description: input.description !== undefined ? input.description : currentDeck.description,
      bookId: currentDeck.bookId,
      userId: currentDeck.userId,
      concurrencyStamp: currentDeck.concurrencyStamp || '',
    };

    const response = await apiClient.put<FlashcardDeckDto>(
      `${FLASHCARD_DECKS_API}/my-deck/${id}`,
      updateDto
    );
    return response.data;
  },

  /**
   * Delete a flashcard deck (current user only)
   */
  async deleteMyDeck(id: string): Promise<void> {
    await apiClient.delete(`${FLASHCARD_DECKS_API}/my-deck/${id}`);
  },
};
